import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  DocumentIcon,
  DocumentArrowUpIcon,
} from "@heroicons/react/24/outline";
import Button from "../common/Button";
import { FormTemplate, FormFieldDefinition, FormSection } from "../../db/schema";
import FormTemplateUpload from "../Forms/FormTemplateUpload";
import FormDesigner from "../Forms/FormDesigner";
import { pickDroppedPdf } from "../Forms/pickDroppedPdf";
import { hasPendingSuggestions, isTemplateProcessing } from "../../utilities/formTemplates";
import { usePageFileDrop } from "../../hooks/usePageFileDrop";
import { FORMS_CHANGED_EVENT } from "../../services/formUploadService";
import { FormDesignerProvider } from "../../context/FormDesignerContext";

type ViewMode = "list" | "designer";

interface AgencyFormTemplateManagerProps {
  // The agency's template API. Defaults to the agency admin's own; the staff
  // portal passes /api/agency-management/agencies/:id/form-templates.
  apiBase?: string;
  // Where templates open when routed by URL (the agency dashboard).
  basePath?: string;
  // Staff portal mode: no URL routing. The open template is held in state and the
  // editor covers the screen inside the surrounding modal. No upload tray: the
  // list itself shows a form as Processing until the AI has read it.
  embedded?: boolean;
}

const AgencyFormTemplateManager: React.FC<AgencyFormTemplateManagerProps> = ({
  apiBase = "/api/agency/form-templates",
  basePath = "/agency/dashboard/forms",
  embedded = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlTemplateId = embedded ? null : location.pathname.split(`${basePath}/`)[1] || null;
  const rootRef = useRef<HTMLDivElement>(null);
  // Embedded: the editor renders inside the surrounding <dialog> (top layer), or
  // it would sit behind it.
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);
  const [overlayEl, setOverlayEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (embedded) setPortalEl((rootRef.current?.closest("dialog") as HTMLElement | null) ?? document.body);
  }, [embedded]);

  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<FormTemplate | null>(null);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  // While the AI works on the open form the designer stays on screen, locked;
  // when that ends it is replaced by a fresh one.
  const designerLocked = !!selectedTemplate && isTemplateProcessing(selectedTemplate);
  const [lockEpoch, setLockEpoch] = useState(0);
  const wasLocked = useRef(designerLocked);
  useEffect(() => {
    if (wasLocked.current && !designerLocked) setLockEpoch((n) => n + 1);
    wasLocked.current = designerLocked;
  }, [designerLocked]);
  const templateUploadModalRef = useRef<HTMLDialogElement>(null) as React.RefObject<HTMLDialogElement>;
  // A PDF dropped on the list opens the upload modal with it loaded.
  const { dragging, dropHandlers } = usePageFileDrop((files) => {
    const pdf = pickDroppedPdf(files);
    if (!pdf) return;
    setDroppedFile(pdf);
    templateUploadModalRef.current?.showModal();
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  // A form being read by the AI is locked until it finishes; keep looking.
  const anyProcessing = templates.some(isTemplateProcessing);
  useEffect(() => {
    if (!anyProcessing) return;
    const timer = setInterval(fetchTemplates, 5000);
    return () => clearInterval(timer);
  }, [anyProcessing]);

  // Uploads finish in the background (tray); show them in the list as they land.
  useEffect(() => {
    window.addEventListener(FORMS_CHANGED_EVENT, fetchTemplates);
    return () => window.removeEventListener(FORMS_CHANGED_EVENT, fetchTemplates);
  }, []);

  // Sync URL templateId → selected template + designer view
  useEffect(() => {
    if (embedded) return; // staff portal: selection lives in state, not the URL
    if (!urlTemplateId) {
      if (viewMode === "designer") {
        setViewMode("list");
        setSelectedTemplate(null);
      }
      return;
    }
    const found = templates.find((t) => t.id === urlTemplateId);
    if (found) {
      setSelectedTemplate(found);
      setViewMode("designer");
    }
  }, [urlTemplateId, templates, embedded]);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(apiBase);
      setTemplates(data.templates || []);
    } catch (error) {
      console.error("Error fetching agency form templates:", error);
      toast.error("Failed to fetch form templates");
    } finally {
      setLoading(false);
    }
  };

  // Open / close a template's editor: by URL on the agency dashboard, by state
  // in the staff portal.
  const openTemplate = (id: string) => {
    if (!embedded) {
      navigate(`${basePath}/${id}`);
      return;
    }
    const found = templates.find((t) => t.id === id);
    if (found) {
      setSelectedTemplate(found);
      setViewMode("designer");
    }
  };
  const closeDesigner = () => {
    if (!embedded) {
      navigate(basePath);
      return;
    }
    setViewMode("list");
    setSelectedTemplate(null);
  };
  // Embedded: keep the open template current as the list refreshes, so its lock
  // state (and finished suggestions) reach the editor.
  useEffect(() => {
    if (!embedded) return;
    setSelectedTemplate((cur) => (cur ? templates.find((t) => t.id === cur.id) ?? null : cur));
  }, [embedded, templates]);

  // Esc inside the editor (e.g. to stop marking options) must not close the whole
  // staff modal around it and throw away the editor's unsaved work.
  const editorOpen = embedded && viewMode === "designer" && !!selectedTemplate;
  useEffect(() => {
    if (!editorOpen || !portalEl || portalEl === document.body) return;
    const keep = (e: Event) => e.preventDefault();
    portalEl.addEventListener("cancel", keep);
    return () => portalEl.removeEventListener("cancel", keep);
  }, [editorOpen, portalEl]);

  const handleGetUploadUrl = async (fileName: string, fileType: string) => {
    const { data } = await axios.post(`${apiBase}/get-upload-url`, {
      fileName,
      fileType,
    });
    return data;
  };

  const handleCreateTemplate = async (templateData: any) => {
    const { data } = await axios.post(apiBase, templateData);
    return data;
  };

  const handleSaveFields = async (fields: FormFieldDefinition[], sections: FormSection[]) => {
    if (!selectedTemplate) return;

    try {
      const { data } = await axios.patch(`${apiBase}/${selectedTemplate.id}`, {
        fields,
        field_sections: sections,
      });
      toast.success("Template fields saved");
      setSelectedTemplate(data);
      await fetchTemplates();
      // Stay in the designer after save so the user can keep editing.
    } catch (error) {
      console.error("Error saving template fields:", error);
      toast.error("Failed to save template fields");
    }
  };

  const handleDelete = (template: FormTemplate) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      await axios.delete(`${apiBase}/${templateToDelete.id}`);
      toast.success("Template deleted");
      await fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    } finally {
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    }
  };

  // The editor. In the staff portal it covers the screen from inside the modal.
  const designer =
    viewMode === "designer" && selectedTemplate ? (
      <FormDesignerProvider>
        <FormDesigner
          // New instance when a lock ends, so it starts from the finished fields.
          key={`${selectedTemplate.id}-${lockEpoch}`}
          lockedByServer={isTemplateProcessing(selectedTemplate)}
          backPath={basePath}
          onBack={embedded ? closeDesigner : undefined}
          template={selectedTemplate}
          initialFields={selectedTemplate.fields || []}
          initialSections={selectedTemplate.field_sections || []}
          onSave={handleSaveFields}
          onCancel={closeDesigner}
          templatesApiBase={apiBase}
          viewUrlEndpoint={`${apiBase}/view-url`}
          portalContainer={embedded ? overlayEl : undefined}
          editPath={(id) => (embedded ? "" : `${basePath}/${id}`)}
          pendingSuggestions={hasPendingSuggestions(selectedTemplate)}
        />
      </FormDesignerProvider>
    ) : null;

  if (designer && !embedded) {
    return <div className="mt-8">{designer}</div>;
  }

  // List view
  if (loading) {
    return <div className="p-6">Loading form templates...</div>;
  }

  return (
    <div
      ref={rootRef}
      className={`p-6 card border border-base-300 bg-base-100 relative ${embedded ? "" : "mt-8"}`}
      {...dropHandlers}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-base-300">
        <div>
          <h2 className="text-2xl font-semibold text-base-content mb-2">
            Form Templates
          </h2>
          <p className="text-base-content/60">
            Upload PDF form templates shared with all officers in your department.
          </p>
        </div>
        <Button onClick={() => templateUploadModalRef.current?.showModal()} variant="primary" size="sm">
          <PlusIcon className="w-4 h-4 mr-2" />
          Upload Template
        </Button>
      </div>

      {/* Template List */}
      <div className="space-y-4">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`p-6 border rounded-lg bg-base-100 border-base-300 transition-all ${
              isTemplateProcessing(template)
                ? "opacity-50 grayscale pointer-events-none select-none"
                : "hover:border-gray-300 dark:hover:border-base-content/30 hover:shadow-md"
            }`}
            aria-disabled={isTemplateProcessing(template)}
          >
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3 flex-1">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <DocumentIcon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-base-content mb-1">
                    {template.name}
                  </h4>
                  <div className="flex items-center gap-2 text-sm text-base-content/60">
                    {hasPendingSuggestions(template) && (
                      <>
                        <span className="text-amber-700 dark:text-amber-400">AI suggestions ready to review</span>
                        <span>&middot;</span>
                      </>
                    )}
                    {isTemplateProcessing(template) && (
                      <>
                        <span className="text-blue-600 dark:text-blue-300 flex items-center gap-1">
                          <span className="loading loading-spinner size-3" /> Processing: reading the form...
                        </span>
                        <span>&middot;</span>
                      </>
                    )}
                    <span>{template.fields?.length || 0} fields</span>
                    <span>&middot;</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        template.is_fillable
                          ? "bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-400"
                          : "bg-yellow-100 dark:bg-yellow-950/40 text-yellow-800 dark:text-amber-400"
                      }`}
                    >
                      {template.is_fillable ? "Fillable" : "Flat"}
                    </span>
                  </div>
                  {template.description && (
                    <p className="text-sm text-base-content/60 mt-1">{template.description}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  disabled={isTemplateProcessing(template)}
                  onClick={() => openTemplate(template.id)}
                  className="p-2 text-base-content/40 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40 rounded-lg transition-colors disabled:opacity-30 disabled:pointer-events-none"
                  title={isTemplateProcessing(template) ? "Still reading this form" : "Edit Fields"}
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  disabled={isTemplateProcessing(template)}
                  onClick={() => handleDelete(template)}
                  className="p-2 text-base-content/40 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors disabled:opacity-30 disabled:pointer-events-none"
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {templates.length === 0 && (
          <div className="text-center text-base-content/60 py-12 bg-base-200 rounded-lg border-2 border-dashed border-gray-300 dark:border-base-300">
            <DocumentArrowUpIcon className="w-12 h-12 mx-auto mb-4 text-base-content/40" />
            <p className="text-lg mb-2">No form templates yet</p>
            <p className="text-sm">
              Upload PDF form templates that all officers in your department can fill out.
            </p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <dialog className={`modal ${showDeleteModal ? "modal-open" : ""}`}>
        <div className="modal-box max-w-md">
          <h3 className="font-bold text-lg">Delete Template</h3>
          <p className="py-4 text-gray-600 dark:text-base-content/70">
            Are you sure you want to delete "{templateToDelete?.name}"? This will
            remove it for all officers in your department.
          </p>
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setTemplateToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete Template
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button
            onClick={() => {
              setShowDeleteModal(false);
              setTemplateToDelete(null);
            }}
          >
            close
          </button>
        </form>
      </dialog>

      <FormTemplateUpload
        modalRef={templateUploadModalRef}
        agencyOnly={true}
        getUploadUrl={handleGetUploadUrl}
        createTemplate={handleCreateTemplate}
        templatesApiBase={apiBase}
        getOpenPath={(id) => (embedded ? "" : `${basePath}/${id}`)}
        droppedFile={droppedFile}
      />
      {embedded && designer && portalEl &&
        createPortal(
          <div ref={setOverlayEl} className="fixed inset-0 z-[1000] bg-base-200 pointer-events-auto">
            {designer}
          </div>,
          portalEl,
        )}
      {dragging && (
        <div className="pointer-events-none absolute inset-2 z-20 rounded-xl border-2 border-dashed border-blue-500 bg-blue-500/5 flex items-start justify-center pt-24">
          <span className="rounded-full bg-blue-500 text-white text-sm font-medium px-4 py-2 shadow-lg">
            Drop a PDF to create a form template
          </span>
        </div>
      )}
    </div>
  );
};

export default AgencyFormTemplateManager;
